import { supabase } from '@/integrations/supabase/client';

export type AuditSource = 'admin_ui' | 'edge_function' | 'import' | 'system' | 'api' | 'bulk';

export interface AuditOptions {
  oldValue?: Record<string, unknown> | null;
  newValue?: Record<string, unknown> | null;
  changedFields?: string[];
  source?: AuditSource;
  correlationId?: string;
  comment?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Compute changed fields by comparing old and new value objects.
 */
function computeChangedFields(
  oldVal?: Record<string, unknown> | null,
  newVal?: Record<string, unknown> | null,
): string[] {
  if (!oldVal || !newVal) return [];
  const fields: string[] = [];
  const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
  for (const key of allKeys) {
    if (JSON.stringify(oldVal[key]) !== JSON.stringify(newVal[key])) {
      fields.push(key);
    }
  }
  return fields;
}

/**
 * Log a single audit event via the `audit-write` edge function.
 *
 * The frontend MUST NOT write directly into `audit_logs`. This helper exists
 * for admin UIs that mutate state through ad-hoc flows where no dedicated
 * write-layer is responsible for emitting the audit row server-side.
 *
 * Whenever a backend write-layer (e.g. `course-write`, `hazard-write`,
 * `content-page-write`, `taxonomy-write`, `*-write`) handles the mutation,
 * the audit row is written by that function and `logAudit` should NOT be
 * called from the client for the same event.
 */
export async function logAudit(
  action: string,
  entityType: string,
  entityId: string,
  opts: AuditOptions = {},
) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return; // silently skip if no user

    const changedFields = opts.changedFields ?? computeChangedFields(opts.oldValue, opts.newValue);

    const { error } = await supabase.functions.invoke('audit-write', {
      body: {
        command: 'write_audit_log',
        payload: {
          action,
          entity_type: entityType,
          entity_id: entityId,
          old_value: opts.oldValue ?? null,
          new_value: opts.newValue ?? null,
          metadata: opts.metadata ?? {},
          correlation_id: opts.correlationId ?? null,
          comment: opts.comment ?? null,
          changed_fields: changedFields.length > 0 ? changedFields : null,
        },
      },
    });
    if (error) {
      console.warn('[audit] Failed to log:', error.message);
    }
  } catch (err) {
    // Audit logging should never break the main flow
    console.warn('[audit] Failed to log:', err);
  }
}

/**
 * Generate a correlation ID for bulk operations.
 */
export function generateCorrelationId(prefix = 'bulk'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Log multiple audit events with the same correlation ID (for bulk operations).
 */
export async function logBulkAudit(
  action: string,
  entityType: string,
  entityIds: string[],
  opts: Omit<AuditOptions, 'correlationId'> & { correlationId?: string } = {},
) {
  const correlationId = opts.correlationId ?? generateCorrelationId();
  for (const entityId of entityIds) {
    await logAudit(action, entityType, entityId, { ...opts, correlationId });
  }
}
